import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  deleteAIReport,
  getAIReportDetails,
  getAIReportQuestionnaire,
  getAIReports,
  postAIReportQuestionnaire,
} from './api';
import { PersonalisedAIReportDetail, PostQuestionnairePayload } from './type';
import { ReportQuestion } from '../../types';

export const useGetAIReport = (profileId?: string) => {
  return useQuery({
    queryKey: ['ai-reports', profileId],
    queryFn: async () => await getAIReports(),
  });
};

export const useInfiniteGetAIReports = (
  profileId: string,
  initialPageParam: number
) => {
  return useInfiniteQuery({
    queryKey: ['inifinite-ai-report', profileId],
    queryFn: async ({ pageParam }) => {
      const response = await getAIReports({
        page_number: pageParam,
        page_size: 10,
      });

      return response;
    },
    initialPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const allData = allPages.flatMap((page) => page.data);
      if (allData.length >= lastPage.count) return undefined;

      return allPages.length + 1;
    },
    select: ({ pages }) => pages.flatMap((page) => page.data),
    gcTime: 0,
    staleTime: 0,
  });
};

interface GetAIReportDetail extends UseQueryOptions {
  tokenId: string;
}
export const useGetAIReportDetail = (
  props: Omit<GetAIReportDetail, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['ai-report-detail', props.tokenId],
    queryFn: async () => await getAIReportDetails(props.tokenId),
    enabled: !!props.tokenId,
    staleTime: Infinity,
    gcTime: Infinity,
    ...props,
  }) as UseQueryResult<PersonalisedAIReportDetail[]>;
};

export const useGetAIReportQuestionnaire = (type: string, q_id?: string) => {
  return useQuery({
    queryKey: ['personalised-ai-questionnaire'],
    queryFn: async () => await getAIReportQuestionnaire(type, q_id),
  });
};

export const usePostAIReportQuestionnaire = (
  props?: Omit<
    UseMutationOptions<
      ReportQuestion,
      Error,
      PostQuestionnairePayload,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postAIReportQuestionnaire,
    ...props,
  });
};

export const useDeleteAIReports = (
  props?: Omit<UseMutationOptions<any, Error, string[], unknown>, 'mutationFn'>
) => {
  return useMutation({
    mutationKey: ['delete-ai-report'],
    mutationFn: deleteAIReport,
    ...props,
  });
};
